import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles } from '@material-ui/core/styles'
import IconContentAdd from '@material-ui/icons/Add'
import IconCancel from '@material-ui/icons/Cancel'
import Alert from '@material-ui/lab/Alert'
import get from 'lodash/get'
import { useCallback, useEffect, useState } from 'react'
import {
  AutocompleteInput,
  BooleanInput,
  Button as RaButton,
  email,
  FileField,
  FileInput,
  FormDataConsumer,
  FormWithRedirect,
  Loading,
  minValue,
  NumberInput,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  useCreate,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Field, useForm } from 'react-final-form'

import { getSettings } from 'api/core'
import { captureExceptionIfProduction, safeMatch } from 'helpers/utils'
import { upload } from '../../api/brokers'
import { BROKER_SUBMISSION_METHOD_CHOICES, BrokerSubmissionMethods } from '../../helpers/constants'
import { validateFEINNumber, validateMCNumber } from '../../helpers/validators'
import {
  BROKER_STATUS_CHOICES,
  BrokerStatus,
  REASONS_REQUIRED_BROKER_STATUSES,
} from '../Brokers/BrokerStatusTransitions'
import { ConditionalInput } from '../Fields'
import { AddressInputForm } from './AddressForm'

const useStyles = makeStyles({
  invoice: {
    display: 'flex',
    alignItems: 'center',
  },
  load: {
    display: 'inline-flex',
    alignItems: 'center',
  },
})

const matchSuggestion = (filter: any, choice: any) => {
  if (!filter || !choice) {
    return null
  }
  const normalizedFilter = filter.toLowerCase().replace(/\.| /g, '')
  const normalizedBrokerName = choice.name.toLowerCase().replace(/\.| /g, '')
  return safeMatch(normalizedBrokerName, normalizedFilter)
}

export const validateCarrierAgreement = (value: any, allValues: any) => {
  if (allValues) {
    const is_carrier_packet_complete = allValues.is_carrier_packet_complete
    if (!value && is_carrier_packet_complete) {
      return 'ra.validation.required'
    }
  }
  return undefined
}

export const validateBolContactEmail = (value: any, allValues: any) => {
  const isBolSubmissionEmail = allValues.bol_submission_method === BrokerSubmissionMethods.Email

  if (isBolSubmissionEmail) {
    return email()(value, allValues) || required()(value, allValues)
  }

  return email()(value, allValues)
}

export const validateCarrierPortalLink = (value: any, allValues: any) => {
  const isBolSubmissionLink = allValues.bol_submission_method === BrokerSubmissionMethods.CarrierPortal

  if (isBolSubmissionLink) {
    return required()(value, allValues)
  }
}

export function BrokerQuickCreateButton({ onChange, redirectToBrokerPage = false }: any) {
  const [showDialog, setShowDialog] = useState(false)
  const [create, { loading }] = useCreate('brokers')
  const [error, setError] = useState<string>('')
  const notify = useNotify()
  const [loadingSettings, setLoadingSettings] = useState(true)
  const [enableFMCSA, setEnableFMCSA] = useState(false)
  const redirect = useRedirect()

  const handleClick = () => setShowDialog(true)

  const handleClose = () => setShowDialog(false)

  useEffect(() => {
    setLoadingSettings(true)
    getSettings().then((settings) => {
      if (settings.broker_fmcsa_switch) {
        setEnableFMCSA(true)
      }
      setLoadingSettings(false)
    })
  }, [])

  const handleSubmit = async (values: any) => {
    const formData: any = new FormData()
    if (values.carrier_agreement) {
      formData.append('carrier_agreement', values.carrier_agreement.rawFile)
      delete values.carrier_agreement
    }

    create(
      { payload: { data: values } },
      {
        onSuccess: ({ data }: any) => {
          onChange(data.id)
          if (formData.carrier_agreement) {
            upload(data.id, formData)
              .then(() => {
                setShowDialog(false)
              })
              .catch((error) => {
                setShowDialog(false)
                notify(
                  `Unable to upload carrier packet: ${error.response.data}. Please attempt to re-upload from the Broker Edit screen.`,
                  { type: 'error' }
                )
                captureExceptionIfProduction(error)
              })
          } else {
            setShowDialog(false)
          }
          if (redirectToBrokerPage) {
            redirect(`/brokers/${data.id}/show/`)
          }
        },
        onFailure: (error: Error) => {
          setError(JSON.stringify(error))
        },
      }
    )
  }

  const validatePaymentTermsDays = [required(), minValue(0)]

  return (
    <>
      <RaButton onClick={handleClick} label="ra.action.create">
        <IconContentAdd />
      </RaButton>
      <Dialog fullWidth open={showDialog} onClose={handleClose} aria-label="Create broker">
        <DialogTitle>Create broker</DialogTitle>
        {loadingSettings && <Loading />}
        {!loadingSettings && (
          <FormWithRedirect
            resource="brokers"
            save={handleSubmit}
            render={({ handleSubmitWithRedirect, saving }) => (
              <>
                <DialogContent>
                  {error && <Alert severity="error">{error}</Alert>}
                  <FormDataConsumer>
                    {({ formData, ...rest }) =>
                      (!enableFMCSA ||
                        formData.is_shipper ||
                        formData.status === BrokerStatus.Invalid.id) && (
                        <TextInput
                          source="name"
                          label="Legal Name"
                          validate={[required()]}
                          fullWidth
                          {...rest}
                        />
                      )
                    }
                  </FormDataConsumer>
                  <BooleanInput source="is_shipper" validate={required()} initialValue={false} />
                  <ConditionalInput when="is_shipper" is={true}>
                    <TextInput
                      source="fein_number"
                      label="FEIN number"
                      validate={validateFEINNumber}
                      fullWidth
                    />
                  </ConditionalInput>
                  <ConditionalInput when="is_shipper" is={false}>
                    <TextInput source="mc_number" label="MC number" validate={validateMCNumber} fullWidth />
                  </ConditionalInput>
                  <SelectInput
                    source="bol_submission_method"
                    choices={BROKER_SUBMISSION_METHOD_CHOICES}
                    validate={required()}
                    fullWidth
                  />
                  <TextInput
                    source="bol_contact_email"
                    label="Invoicing email"
                    validate={email()}
                    fullWidth
                  />
                  <ConditionalInput when="bol_submission_method" is={BrokerSubmissionMethods.CarrierPortal}>
                    <div>
                      <TextInput source="carrier_portal_link" validate={required()} />
                      <br />
                    </div>
                  </ConditionalInput>
                  <NumberInput
                    source="payment_terms_days"
                    label="Payment terms (days)"
                    validate={validatePaymentTermsDays}
                  />
                  <FormDataConsumer>
                    {({ formData, ...rest }) =>
                      (!enableFMCSA || formData.is_shipper) && (
                        <AddressInputForm sourceParent="billing_address" {...rest} />
                      )
                    }
                  </FormDataConsumer>
                  <Field
                    name="is_carrier_packet_complete"
                    subscription={{ value: true }}
                    render={({ input }) => {
                      return (
                        <FileInput
                          source="carrier_agreement"
                          validate={validateCarrierAgreement}
                          isRequired={input.value}
                        >
                          <FileField title="Carrier agreement" />
                        </FileInput>
                      )
                    }}
                  />
                  <BooleanInput
                    source="is_carrier_packet_complete"
                    validate={required()}
                    initialValue={false}
                  />
                  <TextInput
                    source="submission_requirement_notes"
                    label="BOL/Tracking submission notes"
                    fullWidth
                    multiline
                    component="pre"
                  />
                  <SelectInput
                    source="status"
                    label="Status"
                    choices={BROKER_STATUS_CHOICES}
                    validate={required()}
                    fullWidth
                  />
                  <ConditionalInput
                    label="Status Reason"
                    when="status"
                    oneOf={REASONS_REQUIRED_BROKER_STATUSES}
                  >
                    <div>
                      <TextInput source="status_reason" validate={required()} fullWidth />
                      <br />
                    </div>
                  </ConditionalInput>
                  {error && <Alert severity="error">{error}</Alert>}
                  {enableFMCSA && (
                    <Alert severity="info">
                      NOTE: For newly created brokers the legal name, DOT number, and billing address will be
                      auto-filled via FMCSA API.
                    </Alert>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} disabled={loading}>
                    <IconCancel />
                  </Button>
                  <SaveButton
                    handleSubmitWithRedirect={handleSubmitWithRedirect}
                    saving={saving}
                    disabled={loading}
                  />
                </DialogActions>
              </>
            )}
          />
        )}
      </Dialog>
    </>
  )
}

export const BrokerReferenceInputClassName = {
  invoice: 'invoice',
  load: 'load',
  jobApprove: 'jobApprove',
}

// className should be an instance of BrokerReferenceInputClassName
export const BrokerReferenceInput = ({ containerClassName, showQuickCreate = true, ...props }: any) => {
  const classes = useStyles()
  const [version, setVersion] = useState(0)
  const form = useForm()
  const handleChange = useCallback(
    (broker_id) => {
      form.change(props.source, broker_id)
      setVersion(version + 1)
    },
    [version, form, props]
  )
  return (
    <div className={get(classes, containerClassName)}>
      <ReferenceInput key={version} {...props} allowEmpty>
        <AutocompleteInput matchSuggestion={matchSuggestion} />
      </ReferenceInput>
      {showQuickCreate && <BrokerQuickCreateButton onChange={handleChange} />}
    </div>
  )
}
